/** One ticket, as an employee reads it.
 *
 * A card/table hybrid rather than a table row: on a phone a table either scrolls
 * sideways or drops the columns that matter, and this is the surface an employee
 * checks on a phone more than anywhere else. The same component serves the home
 * screen and the full list, so the two cannot drift.
 *
 * ## What it shows, and what it deliberately does not
 *
 * Reference, subject, category, priority, status, when it last moved, and the
 * response target when the ticket has an SLA. Not the assigned team — the portal
 * serializer does not send it, because `test_portal_ticket_payload_exposes_no_
 * internal_fields` classes it with `assigned_agent` as internal. That is
 * invariant 5, and it is enforced server-side rather than trusted here.
 *
 * "Awaiting your reply" gets a visible marker rather than colour alone: the
 * whole point of the row is that some tickets are the employee's turn, and
 * colour-only would fail both a screen reader and anybody with a red/green
 * deficiency.
 */
import { AlertCircle, Clock, MessageSquareReply } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '../../components/ui/badge'
import { Card } from '../../components/ui/card'
import type { TicketSummary } from '../../lib/types'
import { cn } from '../../lib/utils'
import { needsMyReply } from '../activity'
import { PriorityBadge, StatusBadge } from './TicketBits'

/** "2 hours ago". Kept local and small — the console does the same rather than
 *  pulling a date library in for one string. */
function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000))
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ]
  let value = seconds
  for (const [unit, size] of units) {
    if (value < size) {
      return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
        -Math.round(value),
        unit,
      )
    }
    value /= size
  }
  return ''
}

/** The response target, if this ticket has a policy. Null when it does not —
 *  the row then says nothing rather than implying a commitment. */
function responseDue(ticket: TicketSummary): string | null {
  const due = ticket.sla?.response_due_at
  if (!due || ticket.first_response_at) return null
  return relativeTime(due)
}

export function TicketRow({ ticket }: { ticket: TicketSummary }) {
  const mine = needsMyReply(ticket)
  const due = responseDue(ticket)

  return (
    <Link
      to={`/tickets/${ticket.ref}`}
      className="group block rounded-xl focus-visible:outline-none"
    >
      <Card
        className={cn(
          'rounded-xl p-4 transition-all group-hover:border-primary/40 group-hover:shadow-popover group-focus-visible:border-primary group-focus-visible:ring-2 group-focus-visible:ring-ring/30',
          mine && 'border-warning/40 bg-warning/5',
        )}
      >
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="numeric text-xs font-medium text-muted-foreground">
                {ticket.ref}
              </span>
              {mine && (
                // Icon + words, never colour alone.
                <Badge variant="warning" size="sm" className="gap-1">
                  <MessageSquareReply aria-hidden />
                  Your reply needed
                </Badge>
              )}
            </div>

            {/* Long subjects wrap to two lines and then clamp — a 200-character
                title from an email-raised ticket must not push the status off. */}
            <h3 className="mt-1 line-clamp-2 text-sm font-semibold leading-snug">
              {ticket.title}
            </h3>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {ticket.service && (
                <span className="truncate">
                  {ticket.service.service} · {ticket.service.subcategory}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="size-3" aria-hidden />
                Updated {relativeTime(ticket.last_activity_at)}
              </span>
              {due && (
                <span className="inline-flex items-center gap-1 text-warning">
                  <AlertCircle className="size-3" aria-hidden />
                  Response due {due}
                </span>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <PriorityBadge priority={ticket.priority} />
            <StatusBadge status={ticket.status} />
          </div>
        </div>
      </Card>
    </Link>
  )
}
